import dayjs from 'dayjs'
import * as Yup from 'yup'
import { TranslationDictionary } from '@plandi/common'

export const saveProjectValidationSchema = Yup.object().shape({
  name: Yup.string().required('Обязательное поле'),
  cost: Yup.number()
    .test(
      'len',
      'Максимальное количество символов 10',
      (val) => !val || val.toString().length <= 10
    )
    .nullable()
    .when(['status', 'negotiated'], {
      is: (status: string, negotiated: boolean) =>
        status === 'published' && !negotiated,
      then: Yup.number().nullable().required('Обязательное поле')
    }),
  negotiated: Yup.boolean(),
  designYear: Yup.number()
    .min(1960, 'Год проектирования не может быть меньше 1960')
    .max(
      dayjs().year(),
      'Год проектирования не может быть больше текущего года'
    )
    .nullable()
    .when('status', {
      is: 'published',
      then: Yup.number().nullable().required('Обязательное поле')
    }),
  categoryId: Yup.number()
    .nullable()
    .when('status', {
      is: 'published',
      then: Yup.number().nullable().required('Обязательное поле')
    }),
  subCategoryId: Yup.number()
    .nullable()
    .when('status', {
      is: 'published',
      then: Yup.number().nullable().required('Обязательное поле')
    }),
  projectStaging: Yup.string().when('status', {
    is: 'published',
    then: Yup.string().required('Обязательное поле')
  }),
  expertise: Yup.boolean(),
  bimModel: Yup.boolean(),
  fileFormats: Yup.array(
    Yup.string()
      .required('Поле не может быть пустым')
      .matches(
        /^[a-zA-Z0-9]+$/,
        'Может содержать только латинские буквы и цифры'
      )
  ).when('status', {
    is: 'published',
    then: Yup.array().min(1, 'Обязательное поле')
  }),
  landArea: Yup.number()
    .test(
      'len',
      'Максимальное количество символов 8',
      (val) => !val || val.toString().length <= 8
    )
    .nullable()
    .positive('Должно быть положительным числом'),
  builtUpArea: Yup.number()
    .test(
      'len',
      'Максимальное количество символов 8',
      (val) => !val || val.toString().length <= 8
    )
    .nullable()
    .positive('Должно быть положительным числом'),
  buildingArea: Yup.number()
    .test(
      'len',
      'Максимальное количество символов 8',
      (val) => !val || val.toString().length <= 8
    )
    .nullable()
    .positive('Должно быть положительным числом'),
  buildingHeight: Yup.number()
    .test(
      'len',
      'Максимальное количество символов 4',
      (val) => !val || val.toString().length <= 4
    )
    .max(1000, 'Максимальная высота должна быть не более 1000 м')
    .nullable()
    .positive('Должно быть положительным числом'),
  numberOfFloors: Yup.number()
    .test(
      'len',
      'Максимальное количество символов 3',
      (val) => !val || val.toString().length <= 3
    )
    .nullable()
    .positive('Должно быть положительным числом'),
  appointment: Yup.string()
    .max(8192, 'Количество символов в поле должно быть не более 8192')
    .when('status', {
      is: 'published',
      then: Yup.string()
        .min(48, 'Количество символов в поле должно быть не менее 48')
        .required('Обязательное поле')
    }),
  projectComposition: Yup.string()
    .max(8192, 'Количество символов в поле должно быть не более 8192')
    .when('status', {
      is: 'published',
      then: Yup.string()
        .min(48, 'Количество символов в поле должно быть не менее 48')
        .required('Обязательное поле')
    }),
  notes: Yup.string()
    .max(8192, 'Количество символов в поле должно быть не более 8192')
    .when('status', {
      is: 'published',
      then: Yup.string()
        .min(48, 'Количество символов в поле должно быть не менее 48')
        .required('Обязательное поле')
    }),
  parts: Yup.array()
    .max(50, 'Максимальное количество выбранных разделов 50')
    .when('status', {
      is: 'published',
      then: Yup.array().min(1, 'Обязательное поле')
    }),
  status: Yup.string().required(),
  images: Yup.mixed().when('status', {
    is: 'published',
    then: Yup.mixed().required('Обязательное поле')
  }),
  cover: Yup.mixed().when('status', {
    is: 'published',
    then: Yup.mixed().required('Обязательное поле')
  })
})

export const getSaveProjectValidationSchema = (
  translate?: TranslationDictionary['data']
) =>
  Yup.object().shape({
    name: Yup.string().required(translate?.['required-field']),
    cost: Yup.number()
      .test(
        'len',
        translate?.['max-field-length']?.replace('%d', '10') ?? '',
        (val) => !val || val.toString().length <= 10
      )
      .nullable()
      .when(['status', 'negotiated'], {
        is: (status: string, negotiated: boolean) =>
          status === 'published' && !negotiated,
        then: Yup.number().nullable().required(translate?.['required-field'])
      }),
    negotiated: Yup.boolean(),
    designYear: Yup.number()
      //      .min(1960, 'Год проектирования не может быть меньше 1960')
      .min(1960, `${translate?.shop?.['project-page']?.['year-design']} < 1960`)
      .max(
        dayjs().year(),
        `${
          translate?.shop?.['project-page']?.['year-design']
        } > ${dayjs().year()}`
      )
      .nullable()
      .when('status', {
        is: 'published',
        then: Yup.number().nullable().required(translate?.['required-field'])
      }),
    categoryId: Yup.number()
      .nullable()
      .when('status', {
        is: 'published',
        then: Yup.number().nullable().required(translate?.['required-field'])
      }),
    subCategoryId: Yup.number()
      .nullable()
      .when('status', {
        is: 'published',
        then: Yup.number().nullable().required(translate?.['required-field'])
      }),
    projectStaging: Yup.string().when('status', {
      is: 'published',
      then: Yup.string().required(translate?.['required-field'])
    }),
    expertise: Yup.boolean(),
    bimModel: Yup.boolean(),
    fileFormats: Yup.array(
      Yup.string()
        .required(translate?.['required-field'])
        .matches(/^[a-zA-Z0-9]+$/, translate?.['latin-field'] ?? '')
    ).when('status', {
      is: 'published',
      then: Yup.array().min(1, translate?.['required-field'])
    }),
    landArea: Yup.number()
      .test(
        'len',
        translate?.['max-field-length']?.replace('%d', '8') ?? '',
        (val) => !val || val.toString().length <= 8
      )
      .nullable()
      .positive(translate?.['positive-field']),
    builtUpArea: Yup.number()
      .test(
        'len',
        translate?.['max-field-length']?.replace('%d', '8') ?? '',
        (val) => !val || val.toString().length <= 8
      )
      .nullable()
      .positive(translate?.['positive-field']),
    buildingArea: Yup.number()
      .test(
        'len',
        translate?.['max-field-length']?.replace('%d', '8') ?? '',
        (val) => !val || val.toString().length <= 8
      )
      .nullable()
      .positive(translate?.['positive-field']),
    buildingHeight: Yup.number()
      .test(
        'len',
        translate?.['max-field-length']?.replace('%d', '4') ?? '',
        (val) => !val || val.toString().length <= 4
      )
      .max(1000, translate?.['max-height-field']?.replace('%s', '1000'))
      .nullable()
      .positive(translate?.['positive-field']),
    numberOfFloors: Yup.number()
      .test(
        'len',
        translate?.['max-field-length']?.replace('%d', '3') ?? '',
        (val) => !val || val.toString().length <= 3
      )
      .nullable()
      .positive(translate?.['positive-field']),
    appointment: Yup.string()
      .max(8192, translate?.['max-field-length']?.replace('%d', '8192'))
      .when('status', {
        is: 'published',
        then: Yup.string()
          .min(48, translate?.['min-field-length']?.replace('%d', '48'))
          .required(translate?.['required-field'])
      }),
    projectComposition: Yup.string()
      .max(8192, translate?.['max-field-length']?.replace('%d', '8192'))
      .when('status', {
        is: 'published',
        then: Yup.string()
          .min(48, translate?.['min-field-length']?.replace('%d', '48'))
          .required(translate?.['required-field'])
      }),
    notes: Yup.string()
      .max(8192, translate?.['max-field-length']?.replace('%d', '8192'))
      .when('status', {
        is: 'published',
        then: Yup.string()
          .min(48, translate?.['min-field-length']?.replace('%d', '48'))
          .required(translate?.['required-field'])
      }),
    parts: Yup.array()
      .max(50, translate?.['max-field-length']?.replace('%d', '50'))
      .when('status', {
        is: 'published',
        then: Yup.array().min(1, translate?.['required-field'])
      }),
    status: Yup.string().required(),
    images: Yup.mixed().when('status', {
      is: 'published',
      then: Yup.mixed().required(translate?.['required-field'])
    }),
    cover: Yup.mixed().when('status', {
      is: 'published',
      then: Yup.mixed().required(translate?.['required-field'])
    })
  })
